/**
 * QA Panel — floating "Open QA Panel" fallback button.
 *
 * window.open() called outside a user gesture is blocked by every modern
 * browser's popup blocker. So when the panel is auto-activated via URL
 * param or localStorage flag (no gesture), we render a small floating button
 * in the bottom-right that opens the popup on click.
 *
 * Once the popup opens, the button hides itself. If the popup is closed
 * (popup.closed becomes true), the button reappears.
 */

const BUTTON_ID = 'react-lobby-qa-panel-open-btn';

const STYLE = `
    position: fixed;
    bottom: 16px;
    right: 16px;
    z-index: 2147483647;
    padding: 10px 14px;
    background: #2563eb;
    color: white;
    font: 600 12px/1 -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    border: none;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    cursor: pointer;
    user-select: none;
`;

export interface FloatingButton {
    show(): void;
    hide(): void;
    destroy(): void;
}

export function createFloatingButton(onClick: () => void): FloatingButton {
    if (typeof document === 'undefined') {
        return { show: () => undefined, hide: () => undefined, destroy: () => undefined };
    }

    let element: HTMLButtonElement | null = null;

    const ensureElement = (): HTMLButtonElement => {
        if (element) return element;
        const btn = document.createElement('button');
        btn.id = BUTTON_ID;
        btn.type = 'button';
        btn.textContent = 'Open QA Panel';
        btn.setAttribute('style', STYLE);
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            onClick();
        });
        element = btn;
        return btn;
    };

    return {
        show() {
            const el = ensureElement();
            if (!el.isConnected) {
                document.body.appendChild(el);
            }
        },
        hide() {
            if (element?.isConnected) {
                element.parentNode?.removeChild(element);
            }
        },
        destroy() {
            if (element?.isConnected) {
                element.parentNode?.removeChild(element);
            }
            element = null;
        },
    };
}
